import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

import './PageHeader.sass';

import classNames from 'classnames';

import { usePageContext } from './usePageContext';

export type PageHeaderSize = 'medium' | 'large';
export interface PageHeaderCtx {
  size: PageHeaderSize;
}

export interface PageHeaderProps extends React.PropsWithChildren {
  title: string | React.ReactNode;
  breadcrumbs?: React.ReactNode;
  tag?: React.ReactNode;
  actions?: React.ReactNode;
}

type ScrollState = 'normal' | 'hidden' | 'floating';

const SCROLL_THRESHOLD = 10;

function PageHeader({
  title,
  breadcrumbs = undefined,
  tag = undefined,
  actions = undefined,
  children = undefined,
}: PageHeaderProps): JSX.Element {
  const { getTitleComponent } = usePageContext();
  const rootRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);
  const animationFrame = useRef<number | undefined>(undefined);
  const [scrollState, setScrollState] = useState<ScrollState>('normal');

  const TitleComponent = useMemo((): React.ReactNode => {
    switch (typeof title) {
      case 'string': return getTitleComponent(title);
      default: return title;
    }
  }, [title, getTitleComponent]);

  useEffect(() => {
    const root = rootRef.current;
    const parent = root?.parentElement;
    if (!root || !parent) return undefined;

    const updateFloatingPosition = () => {
      const { left, width } = parent.getBoundingClientRect();

      // Fixed-заголовок сохраняет границы контента при изменении ширины страницы.
      root.style.setProperty('--PageHeader-left', `${left}px`);
      root.style.setProperty('--PageHeader-width', `${width}px`);
    };

    const observer = new ResizeObserver(updateFloatingPosition);
    observer.observe(parent);
    updateFloatingPosition();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Заголовок реагирует на прокрутку страницы, а не вложенных панелей и таблиц.
    lastScrollTop.current = window.scrollY;

    const handleScroll = () => {
      if (animationFrame.current !== undefined) return;

      animationFrame.current = window.requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const scrollDelta = scrollTop - lastScrollTop.current;

        animationFrame.current = undefined;

        if (scrollTop <= 0) {
          setScrollState('normal');
          lastScrollTop.current = scrollTop;
          return;
        }

        if (Math.abs(scrollDelta) < SCROLL_THRESHOLD) return;

        setScrollState(scrollDelta > 0 ? 'hidden' : 'floating');
        lastScrollTop.current = scrollTop;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationFrame.current !== undefined) {
        window.cancelAnimationFrame(animationFrame.current);
      }
    };
  }, []);

  return (
    <div
      className={classNames(
        'PageHeaderV2',
        {
          PageHeaderV2_hidden: scrollState === 'hidden',
          PageHeaderV2_floating: scrollState === 'floating',
        },
      )}
      ref={rootRef}
    >

      {!!breadcrumbs && (
        <div>
          {breadcrumbs}
        </div>
      )}

      <div className="PageHeaderV2__Inner">
        <div className="PageHeaderV2__Content">
          <div className="PageHeaderV2__NameContainer">
            {TitleComponent}
            {tag}
          </div>
          {children}
        </div>

        {!!actions && (
          <div className="PageHeaderV2__Actions">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export { PageHeader };
